import React, { createContext, useContext, ReactNode, useState, useCallback } from 'react';

interface MentionedUser {
  id: string;
  username: string;
}

interface MentionContextType {
  mentionedUsers: MentionedUser[];
  addMention: (user: MentionedUser) => void;
  removeMention: (userId: string) => void;
  clearMentions: () => void;
  getMentionedUserIds: () => string[];
  getUserMentionString: () => string;
  formatTextWithMentions: (text: string) => string;
  parseMentionedText: (text: string) => { text: string; mentions: MentionedUser[] };
}

const MentionContext = createContext<MentionContextType | undefined>(undefined);

export const useMention = () => {
  const context = useContext(MentionContext);
  if (!context) {
    throw new Error('useMention must be used within a MentionProvider');
  }
  return context;
};

interface MentionProviderProps {
  children: ReactNode;
}

export const MentionProvider: React.FC<MentionProviderProps> = ({ children }) => {
  const [mentionedUsers, setMentionedUsers] = useState<MentionedUser[]>([]);

  const addMention = useCallback((user: MentionedUser) => {
    setMentionedUsers(prev => {
      // Check if user is already mentioned
      if (prev.some(u => u.id === user.id)) {
        return prev;
      }
      return [...prev, user];
    });
  }, []);

  const removeMention = useCallback((userId: string) => {
    setMentionedUsers(prev => prev.filter(user => user.id !== userId));
  }, []);

  const clearMentions = useCallback(() => {
    setMentionedUsers([]);
  }, []);

  const getMentionedUserIds = useCallback(() => {
    return mentionedUsers.map(user => user.id);
  }, [mentionedUsers]);

  const getUserMentionString = useCallback(() => {
    return mentionedUsers.map(user => `@${user.username}`).join(' ');
  }, [mentionedUsers]);

  const formatTextWithMentions = useCallback((text: string) => {
    let formattedText = text;
    
    // Sort mentions by username length (longest first) to avoid partial replacements
    const sortedUsers = [...mentionedUsers].sort(
      (a, b) => b.username.length - a.username.length
    );
    
    // Replace mentions with formatted version
    sortedUsers.forEach(user => {
      const regex = new RegExp(`@${user.username}\\b`, 'g');
      formattedText = formattedText.replace(
        regex,
        `@[${user.username}](${user.id})`
      );
    });
    
    return formattedText;
  }, [mentionedUsers]);

  const parseMentionedText = useCallback((text: string) => {
    const mentions: MentionedUser[] = [];
    let parsedText = text;
    
    // Find all mentions in the format @[username](userId)
    const mentionRegex = /@\[([^\]]+)\]\(([^)]+)\)/g;
    let match;
    
    while ((match = mentionRegex.exec(text)) !== null) {
      const [fullMatch, username, userId] = match;
      mentions.push({ id: userId, username });
      
      // Replace the formatted mention with just @username
      parsedText = parsedText.replace(fullMatch, `@${username}`);
    }
    
    return { text: parsedText, mentions };
  }, []);

  const value = {
    mentionedUsers,
    addMention,
    removeMention,
    clearMentions,
    getMentionedUserIds,
    getUserMentionString,
    formatTextWithMentions,
    parseMentionedText,
  };

  return (
    <MentionContext.Provider value={value}>
      {children}
    </MentionContext.Provider>
  );
}; 